import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useState, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";

interface DuplicateReviewModalProps {
  row: {
    id: string;
    duplicate: any;
    originals: any[];
  } | null;
  onClose: () => void;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function formatDate(isoDate: string) {
  if (!isoDate) return "—";
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Side-by-side review modal for a transaction flagged `qbImportStatus=duplicate`.
 *
 * Left pane:  the DUPLICATE row (editable).
 * Right pane: the ORIGINAL row(s) already in the system (read-only).
 *
 * Actions:
 *   - "Save edits"          → patches the duplicate row's fields (date,
 *                             amount, description, vendor, reference,
 *                             category) but leaves qbImportStatus alone.
 *   - "Not a duplicate"     → patches qbImportStatus back to "pending" so
 *                             agent_2 re-evaluates it on the next pass
 *                             (and ultimately agent_3 posts it to QB).
 *                             Also saves any pending edits in one shot.
 *   - "Cancel"              → close without persisting.
 *
 * The original side never gets edited from here — that would silently
 * mutate already-imported data. If the operator decides the original
 * is wrong, they should fix it via the regular Transactions page.
 */
export function DuplicateReviewModal({ row, onClose }: DuplicateReviewModalProps) {
  const updateTx = useMutation(api.transactions.update);
  const updateStatus = useMutation(api.transactions.updateImportStatus);

  const dup = row?.duplicate;
  const original = row?.originals?.[0]; // primary candidate; rest shown as siblings

  const [date, setDate] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [reference, setReference] = useState("");
  const [category, setCategory] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Reset form whenever a new row is opened.
  useEffect(() => {
    if (!dup) return;
    setDate(dup.date ?? "");
    setAmount(dup.amount != null ? String(dup.amount) : "");
    setDescription(dup.description ?? "");
    setVendorName(dup.vendorName ?? "");
    setReference(dup.reference ?? "");
    setCategory(dup.category ?? "");
    setError(null);
    setInfo(null);
  }, [dup?._id]);

  if (!row || !dup) return null;

  const buildPatch = () => {
    const patch: any = { id: dup._id };
    if (date && date !== dup.date) patch.date = date;
    const amountNum = parseFloat(amount);
    if (!Number.isNaN(amountNum) && amountNum !== dup.amount) patch.amount = amountNum;
    if (description !== (dup.description ?? "")) patch.description = description;
    if (vendorName !== (dup.vendorName ?? "")) patch.vendorName = vendorName;
    if (reference !== (dup.reference ?? "")) patch.reference = reference || undefined;
    if (category !== (dup.category ?? "")) patch.category = category || undefined;
    return patch;
  };

  const handleSaveEdits = async () => {
    setError(null);
    setInfo(null);
    setSaving(true);
    try {
      const patch = buildPatch();
      if (Object.keys(patch).length > 1) {
        await updateTx(patch);
        setInfo("Edits saved.");
      } else {
        setInfo("No changes to save.");
      }
    } catch (e: any) {
      setError(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleNotDuplicate = async () => {
    setError(null);
    setInfo(null);
    setSaving(true);
    try {
      // 1) flush any pending edits first
      const patch = buildPatch();
      if (Object.keys(patch).length > 1) {
        await updateTx(patch);
      }
      // 2) flip status — back to "pending" puts it back in the QB-import
      //    pipeline. agent_2 will re-evaluate it (and now find no dup
      //    because the operator already confirmed it isn't one).
      await updateStatus({ id: dup._id, qbImportStatus: "pending" });
      setInfo("Reclassified — will be re-evaluated on the next QB import pass.");
      setTimeout(onClose, 800);
    } catch (e: any) {
      setError(e?.message ?? "Reclassify failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CompareArrowsIcon color="warning" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Review duplicate transaction
        </Typography>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`${row.originals.length} candidate original${row.originals.length === 1 ? "" : "s"}`}
        />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {info && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* ============= LEFT PANE — duplicate (editable) ============= */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: "2px solid", borderColor: "warning.light", borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Chip label="DUPLICATE" color="warning" size="small" />
                <Typography variant="caption" color="text.secondary">
                  flagged on import — editable
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                <TextField
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  size="small"
                  fullWidth
                  inputProps={{ step: "0.01" }}
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                />
                <TextField
                  label="Vendor name"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="(optional)"
                />
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Source file:{" "}
                <code style={{ fontSize: 11 }}>
                  {dup.sourceFileId ?? "—"}
                </code>
                <br />
                Tx ID:{" "}
                <code style={{ fontSize: 11 }}>{dup._id}</code>
              </Typography>
            </Box>
          </Grid>

          {/* ============= RIGHT PANE — original (read-only) ============= */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Chip label="ORIGINAL" color="success" size="small" />
                <Typography variant="caption" color="text.secondary">
                  already imported — read-only
                </Typography>
              </Stack>

              {original ? (
                <Stack spacing={1.5}>
                  <ReadOnlyField label="Date" value={formatDate(original.date)} />
                  <ReadOnlyField
                    label="Amount"
                    value={formatCurrency(original.amount)}
                    valueColor={original.amount < 0 ? "error.main" : "success.main"}
                  />
                  <ReadOnlyField
                    label="Description"
                    value={original.description ?? "—"}
                    multiline
                  />
                  <ReadOnlyField label="Vendor name" value={original.vendorName ?? "—"} />
                  <ReadOnlyField label="Reference" value={original.reference ?? "—"} />
                  <ReadOnlyField label="Category" value={original.category ?? "—"} />
                  <ReadOnlyField
                    label="QB import status"
                    value={original.qbImportStatus ?? "—"}
                  />

                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Source file:{" "}
                    <code style={{ fontSize: 11 }}>
                      {original.sourceFileId ?? "—"}
                    </code>
                    <br />
                    Tx ID:{" "}
                    <code style={{ fontSize: 11 }}>{original._id}</code>
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="warning" variant="outlined">
                  No candidate original found. The row that triggered the duplicate
                  flag was probably deleted by a recovery sweep. If this is a
                  legitimate charge, use <strong>Not a duplicate</strong> below to
                  put it back in the pipeline.
                </Alert>
              )}

              {row.originals.length > 1 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    {row.originals.length - 1} more match{row.originals.length - 1 === 1 ? "" : "es"} on
                    the same (date, amount, vendor) — uncommon, may signal a
                    real-world repeat charge (subscription, batch). Verify
                    against the bank statement.
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleSaveEdits}
          disabled={saving}
          variant="outlined"
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          Save edits
        </Button>
        <Button
          onClick={handleNotDuplicate}
          disabled={saving}
          variant="contained"
          color="warning"
          startIcon={<RestartAltIcon />}
        >
          Not a duplicate — re-import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReadOnlyField({
  label,
  value,
  valueColor,
  multiline,
}: {
  label: string;
  value: string;
  valueColor?: string;
  multiline?: boolean;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: valueColor,
          whiteSpace: multiline ? "pre-wrap" : "nowrap",
          overflow: multiline ? "visible" : "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
